import type { FieldValues, Path, UseFormSetError } from 'react-hook-form'
import { ApiError } from './api'

/**
 * Maps a 422 problem `errors` map onto react-hook-form fields.
 * Laravel dotted keys (`address.city`, `permissions.0`) are mapped to the
 * closest known field; unknown keys fall back to `root.server`.
 *
 * @returns true if the error was a validation error and has been applied.
 */
export function applyServerErrors<T extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<T>,
  options: { fields?: readonly string[]; aliases?: Record<string, string> } = {},
): boolean {
  if (!(error instanceof ApiError) || !error.isValidation) return false
  const entries = Object.entries(error.errors)
  const unmatched: string[] = []
  let focused = false

  for (const [rawKey, messages] of entries) {
    const message = messages[0]
    if (!message) continue
    const key = resolveField(rawKey, options)
    if (key) {
      setError(key as Path<T>, { type: 'server', message }, { shouldFocus: !focused })
      focused = true
    } else {
      unmatched.push(message)
    }
  }

  if (unmatched.length > 0 || entries.length === 0) {
    setError('root.server' as Path<T>, { type: 'server', message: unmatched[0] ?? error.detail })
  }
  return true
}

function resolveField(rawKey: string, { fields, aliases }: { fields?: readonly string[]; aliases?: Record<string, string> }): string | null {
  if (aliases?.[rawKey]) return aliases[rawKey]
  if (!fields) return rawKey
  if (fields.includes(rawKey)) return rawKey
  // `permissions.3` → `permissions`
  const base = rawKey.split('.')[0] ?? rawKey
  if (aliases?.[base]) return aliases[base]
  if (fields.includes(base)) return base
  return null
}
